<template>
  <main class="main main--has-floating-buttons">
    <template v-if="room !== null">
      <portal to="headerTitle">
        {{ room.roomNumber }}
      </portal>
      <card :title="$t('admin.pageRoom.card.name')" cols="12 6@desktop">
        <hm-card-inner>
          <hm-input v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                    v-model="room.id"
                    :disabled="true"
                    :label="$t('admin.pageRoom.id')"
                    name="id"
                    cols="12" />
          <hm-input v-model="room.roomNumber"
                    :lazy="true"
                    :disabled="room.deleted"
                    :label="$t('admin.pageRoom.roomNumber')"
                    :help="$t('admin.pageRoom.roomNumber_help')"
                    validate="required"
                    name="roomNumber"
                    cols="12" />
          <hm-input v-model="room.name"
                    :lazy="true"
                    :disabled="room.deleted"
                    :label="$t('admin.pageRoom.name')"
                    :help="$t('admin.pageRoom.name_help')"
                    validate="required"
                    name="name"
                    cols="12" />
          <hm-textarea v-model="room.description"
                       :lazy="true"
                       :disabled="room.deleted"
                       :label="$t('admin.pageRoom.description')"
                       name="description"
                       cols="12" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.pageRoom.card.options')" cols="12 6@desktop">
        <hm-card-inner>
          <hm-input-select v-model="room.roomTypeId"
                           :disabled="room.deleted"
                           :label="$t('admin.pageRoom.roomType')"
                           name="roomType"
                           cols="12">
            <option v-for="item in hotelSettings.roomTypes.all(room.roomTypeId)" v-if="!item.deleted" :value="item.id" v-html="item.name[$shortLocale]"></option>
          </hm-input-select>
          <hm-input-number v-model="room.emptyLinesAfter"
                           :lazy="true"
                           :disabled="room.deleted"
                           :label="$t('admin.pageRoom.emptyLinesAfter')"
                           :help="$t('admin.pageRoom.emptyLinesAfter_help')"
                           validate="required"
                           name="emptyLinesAfter"
                           cols="12" />
          <hm-input v-model="room.websiteICalLink"
                    :disabled="true"
                    :label="$t('admin.pageRoom.websiteICalLink')"
                    :help="$t('admin.pageRoom.websiteICalLink_help')"
                    name="websiteICalLink"
                    cols="12">
            <template slot="icon-inline-right">
              <input-icon-button :border="true" :title="$t('admin.pageRoom.copyToClipboard')" icon="copy" @click="$copy(room.websiteICalLink)" />
            </template>
          </hm-input>
        </hm-card-inner>
      </card>

      <card v-if="hasLicense('iCalImport')" :title="$t('admin.pageRoom.card.icalInterface')" cols="12" card-type="collapsible">
        <template v-for="iCal in room.iCal">
          <hm-card-inner :title="iCal.name" :id="iCal.id" :key="iCal.id">
            <hm-input v-model="iCal.iCalLink"
                      :disabled="true"
                      :label="$t('admin.pageRoom.iCalLink')"
                      name="iCalLink"
                      cols="12">
              <template slot="icon-inline-right">
                <input-icon-button :border="true" :title="$t('admin.pageRoom.copyToClipboard')" icon="copy" @click="$copy(iCal.iCalLink)" />
              </template>
            </hm-input>
            <hm-input :lazy="true"
                      v-model="iCal.iCalImportLink"
                      :disabled="room.deleted"
                      :label="$t('admin.pageRoom.iCalImportLink')"
                      name="iCalImportLink"
                      cols="12">
            </hm-input>
          </hm-card-inner>
        </template>
      </card>

      <card v-if="!isEmpty(room.history)"
            id="history"
            :title="$t('admin.pageRoom.card.editHistory')"
            cols="12"
            card-type="collapsible">
        <hm-card-inner :full-size="true">
          <history-table :history="room.history" />
        </hm-card-inner>
      </card>

      <hm-floating-buttons id="actions" ref="floatingButtons">
        <hm-floating-button v-if="!room.deleted"
                            :label="$t('admin.pageRoom.actions.deleteRoom')"
                            icon="delete"
                            color="error"
                            @click="deleteRoom(false)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmInputNumber from '../../lib/inputs/HmInputNumber';
import HmCardInner from '../../lib/layout/HmCardInner';

export default {
  name: 'PageRoom',
  components: { HmCardInner, HmInputNumber, HmFloatingButton },
  autosave: [
    {
      name: 'room',
      variable: 'room',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.room.deleted === true) return;

        const toSave = cloneDeep(this.room);

        this.axios.put(this.serviceUrls.room.get(this, this.roomId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
            this.$serviceBus.$emit('room.list');
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      room: null,
      roomId: this.$route.params.roomId,
    };
  },

  computed: {
  },

  watch: {
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.room.get(this, this.roomId))
        .then((response) => {
          if (isNil(this.room)) {
            this.room = response.data.data;
          }
        });
    },
    deleteRoom(confirmed = false) {
      if (confirmed === true) {
        this.axios.delete(this.serviceUrls.room.get(this, this.roomId))
          .then((response) => {
            this.room = response.data.data;
            this.showSuccessMessage();
            this.$serviceBus.$emit('room.list');
            this.$serviceBus.$emit('hotelSettings.update');
            this.$router.push({
              name: 'rooms',
            });
          })
          .catch((error) => {
            this.showMessages(error.response);
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
          });
      } else {
        this.showConfirmModal(
          this.$t('admin.pageRoom.confirmDeleteRoom'),
          (doNotShowAgain) => {
            this.deleteRoom(true);
          },
        );
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageRoom.vue